import { FlightsFareTypes } from 'constants/flight'
import { getFareTypeLabel } from 'hooks/Flights/useFlightFareLabels'
import type { IntlShape } from 'react-intl'
import { INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import { last, sum } from 'lib/array/arrayUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { arrayToStringListFormat } from 'lib/string/stringUtils'

interface Supplement {
  validatingCarrierName: string
  validatingCarrierLogo: string
}

export function buildFlightItemBreakdownView(
  itemView: App.Checkout.FlightItemView,
  segment: App.Checkout.FlightItemViewJourney,
  supplement: Supplement,
  intl: IntlShape,
  useLuxPlusRegularPrice = false,
): App.Checkout.FlightItemBreakdownView {
  const isSelectedLuxPlusFare = !!segment.fareFamily?.bookingInfo?.isLuxPlusFare || segment.journeyFlight.isLuxPlusFare
  const regularPrice = segment.fareFamily?.regularCost?.all ?? segment.journeyFlight.regularPrice?.all.totalFare
  const price = useLuxPlusRegularPrice && isSelectedLuxPlusFare ? regularPrice : undefined
  return {
    itemId: itemView.item.itemId,
    title: formatJourneyShorthand(segment.journeyFlight, intl),
    additionalInfo: [{ text: formatJourneyDepartureDate(segment.journeyFlight, intl) }],
    itemType: 'flight' as const,
    additionalElements: [],
    price: price ?? (segment.cost || segment.journeyFlight.cost),
    journey: segment.journeyFlight,
    validatingCarrierLogo: supplement.validatingCarrierLogo,
    validatingCarrierName: supplement.validatingCarrierName,
    isBundled: itemView.isBundled,
    taxesAndFees: itemView.totals.taxesAndFees,
    fareFamily: segment.fareFamily?.fareFamily.description,
    itemView,
  }
}

export function buildDateChangeFlightItemBreakdownView(
  itemView: App.Checkout.FlightItemView,
  intl: IntlShape,
): App.Checkout.FlightsDateChangeFeeBreakdownView {
  const passengers = itemView.passengers
  const flights = itemView.flights

  const travellerCounts = {
    adult: passengers.filter(p => p.type === 'adult').length,
    child: passengers.filter(p => p.type === 'child').length,
    infant: passengers.filter(p => p.type === 'infant').length,
  }

  const priceMap = {
    adult: sum(flights.map(f => f.journeyFlight.priceBreakdown?.adults.cost ?? 0)) * travellerCounts.adult,
    child: sum(flights.map(f => f.journeyFlight.priceBreakdown?.children?.cost ?? 0)) * travellerCounts.child,
    infant: sum(flights.map(f => f.journeyFlight.priceBreakdown?.infants?.cost ?? 0)) * travellerCounts.infant,
  }

  const additionalElements = Object.entries(travellerCounts)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: formatTravellerCount(intl, type, count),
      },
      right: {
        type: 'price' as const,
        value: priceMap[type as keyof typeof priceMap],
      },
    }))

  return {
    itemType: 'flights-date-change-fee' as const,
    title: '',
    additionalInfo: [],
    additionalElements,
  }
}

export function formatJourneyDepartureDate(journey: App.JourneyFlight, intl: IntlShape): string {
  return intlDateFormatter(journey.flights[0].departingDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR, intl.locale)
}

export function formatFareType(intl: IntlShape, type: 'return' | 'oneWay' | 'multiCity'): string {
  const titleNoun = intl.formatMessage({
    id: 'BKAVU9GngwKfqMEpyERq',
    defaultMessage: '{fareCount, plural, one {flight} other {flights}}',
  }, {
    fareCount: type === FlightsFareTypes.ONE_WAY ? 1 : 2,
  })
  return `${getFareTypeLabel(intl, type)} ${titleNoun}`
}

export function formatJourneyShorthand(journey: App.JourneyFlight, intl: IntlShape): string {
  const journeyDepartureLocation = journey.flights[0].departingDisplayNames.primary
  const journeyArrivalLocation = last(journey.flights).arrivalDisplayNames.primary
  return intl.formatMessage(
    {
      id: 'g2PJadYFAbVzD15ccje9',
      defaultMessage: '{journeyDepartureLocation} to {journeyArrivalLocation}',
    },
    {
      journeyDepartureLocation,
      journeyArrivalLocation,
    },
  )
}

export function formatJourneySummaryTitle(
  journeys: Array<App.Checkout.FlightItemViewJourney>,
  fareType: FlightsFareTypes,
  intl: IntlShape,
): string {
  if ([FlightsFareTypes.RETURN, FlightsFareTypes.ONE_WAY].includes(fareType)) {
    return formatJourneyShorthand(journeys[0].journeyFlight, intl)
  }

  const departureLocation = journeys[0].journeyFlight.flights[0].departingDisplayNames.primary
  const lastJourney = last(journeys)
  const arrivalLocation = last(lastJourney.journeyFlight.flights).arrivalDisplayNames.primary

  return intl.formatMessage(
    {
      id: '5jImSQ8cuO-RBlJWztKF',
      defaultMessage: '{departureLocation} to {arrivalLocation}',
    },
    {
      departureLocation,
      arrivalLocation,
    },
  )
}

export function formatTravellerCount(intl: IntlShape, type: string, count: number): string {
  switch (type) {
    case 'adult':
      return intl.formatMessage(
        { id: 'KYBPIR6p9xMDUuKl7muZ', defaultMessage: '{count, plural, one {# adult} other {# adults}}' },
        { count },
      )
    case 'child':
      return intl.formatMessage(
        { id: 'YpTzOh-I49WKcuCt-5M3', defaultMessage: '{count, plural, one {# child} other {# children}}' },
        { count },
      )
    case 'infant':
      return intl.formatMessage(
        { id: '6EGVLkiJYwvBk1m9K2W1', defaultMessage: '{count, plural, one {# infant} other {# infants}}' },
        { count },
      )
    default:
      return `${count} ${type}`
  }
}

export function formatFlightPassengersShorthand(passengers: Array<App.Checkout.FlightPassenger>, intl: IntlShape): string {
  const paxMap = passengers.reduce<{ [type: string]: number }>((acc, item) => {
    return { ...acc, [item.type]: item.type in acc ? acc[item.type] + 1 : 1 }
  }, {})
  const elements = Object.keys(paxMap).map(key => {
    const count = paxMap[key]
    if (key === 'adult') {
      return intl.formatMessage({
        id: 'juX3oM3IvMi2REO3A_fy',
        defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
      }, { count })
    }

    if (key === 'child') {
      return intl.formatMessage({
        id: 'Y2EKww9_ElDEMz-kMCP_',
        defaultMessage: '{count, plural, one {# Child} other {# Children}}',
      }, { count })
    }

    return intl.formatMessage({
      id: '3Si7O_TKC4ZPmZ649H4O',
      defaultMessage: '{count, plural, one {# Infant} other {# Infants}}',
    }, { count })
  })
  return arrayToStringListFormat(elements, 'and', intl)
}
